import { useState, useEffect } from 'react';
import { Room } from '../types/user';
import { useRooms } from '../context/RoomContext';
import { signalRService } from '../services/signalr/SignalRService';
import { HubEvents, SignalRRoom } from '../services/signalr/types';
import './PendingApprovalModal.css';

interface PendingApprovalModalProps {
  room: Room;
  requestId: string;
  onCancel: () => void;
  onApproved: (room: Room) => void;
  onDenied: (reason?: string) => void;
}

export function PendingApprovalModal({ room, requestId, onCancel, onApproved, onDenied }: PendingApprovalModalProps) {
  const { cancelJoinRequest } = useRooms();
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    // Listen for approval/denial events
    const unsubApproved = signalRService.on<[string, SignalRRoom]>(HubEvents.JoinRequestApproved, (roomId, signalRRoom) => {
      if (roomId === room.id) {
        // Map the SignalR room to our Room type
        const mappedRoom: Room = {
          id: signalRRoom.id,
          code: signalRRoom.code ?? '',
          name: signalRRoom.name,
          description: signalRRoom.description,
          context: signalRRoom.context as Room['context'],
          hostId: signalRRoom.hostId,
          hostName: signalRRoom.hostName,
          minAge: signalRRoom.settings?.minAge ?? 18,
          maxAge: signalRRoom.settings?.maxAge ?? 99,
          maxPlayers: signalRRoom.maxPlayers,
          currentPlayers: signalRRoom.currentPlayers,
          players: (signalRRoom.players ?? []).map((p) => ({
            id: p.id,
            displayName: p.displayName,
            avatar: p.avatar,
            characterType: p.characterType,
            gender: p.gender as 'male' | 'female' | undefined,
            isHost: p.isHost,
            joinedAt: new Date(p.joinedAt),
          })),
          tags: signalRRoom.tags ?? [],
          isPrivate: signalRRoom.isPrivate,
          hasPassword: signalRRoom.hasPassword ?? false,
          requiresApproval: signalRRoom.requiresApproval ?? false,
          createdAt: new Date(signalRRoom.createdAt),
          status: signalRRoom.status,
        };
        onApproved(mappedRoom);
      }
    });

    const unsubDenied = signalRService.on<[string, string | undefined]>(HubEvents.JoinRequestDenied, (roomId, reason) => {
      if (roomId === room.id) {
        onDenied(reason);
      }
    });

    return () => {
      unsubApproved();
      unsubDenied();
    };
  }, [room.id, onApproved, onDenied]);

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      await cancelJoinRequest(requestId);
      onCancel();
    } catch (e) {
      console.error('Failed to cancel request:', e);
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content pending-approval-modal">
        <div className="pending-icon">
          <div className="pulse-ring"></div>
          <span>&#x1F6AA;</span>
        </div>

        <h2>Waiting for Approval</h2>

        <p className="room-name">
          Requesting to join <strong>{room.name}</strong>
        </p>

        <p className="waiting-text">
          The host needs to approve your request to join this room.
          Please wait...
        </p>

        <div className="room-info">
          <div className="info-item">
            <span className="label">Host</span>
            <span className="value">{room.hostName}</span>
          </div>
          <div className="info-item">
            <span className="label">Players</span>
            <span className="value">{room.currentPlayers}/{room.maxPlayers}</span>
          </div>
        </div>

        <button
          className="cancel-btn"
          onClick={handleCancel}
          disabled={isCancelling}
        >
          {isCancelling ? 'Cancelling...' : 'Cancel Request'}
        </button>
      </div>
    </div>
  );
}
